import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {VoiceProfilePillsProps} from './voice-profile-pills.types';
import {MAX_VISIBLE_PILLS, MAX_VISIBLE_PILLS_FULL_SCREEN, VOICE_PROFILES_DROPDOWN_LEFT_OFFSET, VOICE_PROFILES_MODAL_HEIGHT} from './voice-profile-pills.types';
import type {PlayableSubfilter} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {getVoiceFilters} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {VoiceProfilePill} from '@Panels/ai-voice-panel/components/voice-profile-pill';
import styles from './voice-profile-pills.module.scss';
import {Dropdown} from '@Components/dropdown-v2';
import {VoiceProfilesDropdownPopup} from '@Panels/ai-voice-panel/components/voice-profiles-dropdown-popup';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import {isMobile} from 'react-device-detect';

export function VoiceProfilePills({...props}: VoiceProfilePillsProps): ReactElement<HTMLDivElement> {
  const maxVisiblePills = props.isFullScreen ? MAX_VISIBLE_PILLS_FULL_SCREEN : MAX_VISIBLE_PILLS;
  const voiceFilters = getVoiceFilters();
  const selectedProfile = voiceFilters.find((profile) => profile.id === props.selectedProfileId);
  const remainingProfiles = voiceFilters.filter((profile) => profile.id !== props.selectedProfileId).slice(0, maxVisiblePills - 1);
  const visibleProfiles = selectedProfile ? [selectedProfile, ...remainingProfiles] : voiceFilters.slice(0, maxVisiblePills);
  const [isOpen, setIsOpen] = useState(false);

  const getAvatarUrl = (profile: PlayableSubfilter): string => {
    return profile.avatarUrl ?? getRandomThumbnailImageForAudioItem();
  };

  const getAllVoiceProfiles = (isSmallScreen: boolean): ReactElement => {
    return (
      <VoiceProfilesDropdownPopup
        selectedProfileId={props.selectedProfileId}
        onProfileSelected={(id) => {
          props.onProfileSelected(id);
        }}
        isSmallScreen={isSmallScreen}
      />
    );
  };

  const getMoreProfilesDropdown = (): ReactElement<HTMLDivElement> => {
    return (
      <Dropdown
        selector={<Icon icon={'icon-more-h'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} type={IconType.SECONDARY} className={styles.moreButton} isActive={isOpen} />}
        popup={getAllVoiceProfiles(false)}
        open={isOpen}
        onOpen={() => {
          setIsOpen(true);
        }}
        onClose={() => {
          setIsOpen(false);
        }}
        popUpHasCustomWidth
        position={DROPDOWN_POSITION.FIXED}
        popupClassName={styles.voiceProfiles}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        leftOffset={VOICE_PROFILES_DROPDOWN_LEFT_OFFSET}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_voice_profile')}
        modalContent={getAllVoiceProfiles(true)}
        modalClassName={'spacing-p-t-0 spacing-p-l-3 spacing-p-r-3 spacing-p-b-3'}
        modalHeight={VOICE_PROFILES_MODAL_HEIGHT}
        modalShowCloseButton={false}
        modalShowBackButton={true}
        closeModalOnBack={true}
      />
    );
  };

  const getMoreProfiles = (): ReactElement => {
    if (isMobile) {
      return getMoreProfilesDropdown();
    }
    return (
      <OverlayTrigger
        overlay={
          <Tooltip id={`ai-voice-profiles-dropdown`} className={`radius-8 ${styles.tooltip}`}>
            <div className={'flex-row-align-center flex-justify-between'}>
              <Text val={window.i18next.t('pmwjs_voice_profiles')} size={TextSize.XSMALL} />
            </div>
          </Tooltip>
        }
        offset={[0, 4]}
        trigger={['hover', 'focus']}
      >
        <div>{getMoreProfilesDropdown()}</div>
      </OverlayTrigger>
    );
  };

  return (
    <div className={`flexbox flex-items-center spacing-m-t-3 ${styles.container}`}>
      {visibleProfiles.map((profile) => {
        return (
          <VoiceProfilePill
            key={profile.id}
            avatarUrl={getAvatarUrl(profile)}
            name={profile.name}
            soundtrack={profile.soundtrack}
            isSelected={props.selectedProfileId === profile.id}
            onClick={() => {
              props.onProfileSelected(profile.id);
            }}
          />
        );
      })}
      {getMoreProfiles()}
    </div>
  );
}
